import { useEffect, useState } from "react";
import { callApi } from "../../services/ApiServices";
import { ErrorMessage, validateRequired } from "../../utlis/function";
import { useGlobalStates } from "../../utlis/ClubsContext";
import CommonLoader from "../common/CommonLoader";

const EditAdvertiserBenefitsModal = ({ isOpen, onClose, initialData }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedClub, handleSelectedClubDetail } = useGlobalStates();

  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: initialData?.title || "",
        description: initialData?.description || "",
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, initialData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!validateRequired(formData.title)) {
      newErrors.title = "Title is required";
    }

    if (!validateRequired(formData.description)) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const isFormChanged =
      formData.title !== (initialData?.title || "") ||
      formData.description !== (initialData?.description || "");

    if (!isFormChanged) {
      onClose();
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await callApi({
        method: "put",
        url: `club/update/${selectedClub?.id}`,
        data: {
          fields: {
            advertiserBenefitsTitle: formData.title,
            advertiserBenefitsDescription: formData.description,
          },
        },
      });

      if (response.status === 200) {
        await handleSelectedClubDetail(selectedClub);
        onClose();
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {isSubmitting && <CommonLoader fullScreen />}
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto main-shadow">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-xl md:text-3xl font-medium text-black tracking-[0] leading-[normal]">
            Advertiser Benefits Section
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#4C4C4C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <p className="px-6 text-xl text-textcolor">
          Update the advertiser-facing benefits content shown in club details.
        </p>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label
              htmlFor="advertiser-benefits-title"
              className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
            >
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="advertiser-benefits-title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="Enter title"
              className={`w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border transition-colors ${
                errors.title
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-primary"
              } focus:outline-none`}
            />
            <ErrorMessage message={errors.title} />
          </div>

          <div>
            <label
              htmlFor="advertiser-benefits-description"
              className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="advertiser-benefits-description"
              rows="5"
              value={formData.description}
              onChange={(e) =>
                handleInputChange("description", e.target.value)
              }
              placeholder="Enter description"
              className={`w-full px-4 py-3 bg-white rounded-2xl text-base font-normal text-black border transition-colors resize-none ${
                errors.description
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-primary"
              } focus:outline-none`}
            />
            <ErrorMessage message={errors.description} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 rounded-full text-base font-medium text-textcolor hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-primary text-white rounded-full text-base font-medium hover:opacity-90 transition-opacity"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdvertiserBenefitsModal;
